// Collection schedule and data retention (ENTRAID-269).
//
// The Settings page saves these to the customer config; the portal applies
// them to the customer's stack as the ScheduleExpression (the EventBridge
// rule) and DataRetentionDays (the S3 lifecycle Glacier transition)
// parameters of infrastructure/client-stack.yaml. One module so the page,
// the API validation, the stack parameters and the data reference agree.
//
// Pure, no imports: used by the SPA and the portal API.

export const DEFAULT_SCHEDULE = 'cron(0 7 * * ? *)';
export const DEFAULT_RETENTION_DAYS = 365;

// The stack moves files to S3 Standard-IA after 90 days and to Glacier after
// DataRetentionDays. Amazon S3 requires the Glacier transition to come at
// least 30 days after the Standard-IA one in the same rule, so the smallest
// valid value is 120. 0 turns the transition off (keep everything in fast
// storage).
export const MIN_RETENTION_DAYS = 120;
export const MAX_RETENTION_DAYS = 36500;

export const SCHEDULE_PRESETS = [
  { id: 'daily', label: 'Daily at 07:00 UTC (recommended)', cron: DEFAULT_SCHEDULE, time: '07:00 UTC', cadence: 'daily' },
  { id: 'twice-daily', label: 'Twice daily (07:00 and 19:00 UTC)', cron: 'cron(0 7,19 * * ? *)', time: '07:00 and 19:00 UTC', cadence: 'twice a day' },
  { id: 'every-6-hours', label: 'Every 6 hours', cron: 'cron(0 0,6,12,18 * * ? *)', time: '00:00, 06:00, 12:00 and 18:00 UTC', cadence: 'every 6 hours' },
  { id: 'weekly', label: 'Weekly, Sunday at 22:00 UTC', cron: 'cron(0 22 ? * SUN *)', time: 'Sunday 22:00 UTC', cadence: 'weekly' },
];

const FIELD_RE = /^[0-9A-Z*?,/#L-]+$/;

/**
 * Returns an error message, or null when `expr` is a schedule the stack can
 * take: an EventBridge cron with six fields, one of day-of-month and
 * day-of-week set to '?', and a single minute value, so it runs at most
 * once an hour (a collection run takes up to 15 minutes and pulls full
 * snapshots from Microsoft Graph). rate() expressions are not accepted.
 */
export function validateSchedule(expr) {
  if (typeof expr !== 'string') return 'schedule must be a cron(...) expression';
  const m = expr.trim().match(/^cron\((.+)\)$/);
  if (!m) return 'schedule must be an EventBridge cron(...) expression, for example cron(0 7 * * ? *)';
  const fields = m[1].trim().split(/\s+/);
  if (fields.length !== 6) return 'cron(...) needs six fields: minutes hours day-of-month month day-of-week year';
  if (!fields.every((f) => FIELD_RE.test(f.toUpperCase()))) return 'cron(...) has characters EventBridge does not accept';
  const [minutes, hours, dom, , dow] = fields;
  if (!/^[0-9]{1,2}$/.test(minutes) || Number(minutes) > 59) {
    return 'the minutes field must be a single value from 0 to 59 (runs at most once an hour)';
  }
  if (hours !== '*' && !/^[0-9,/-]+$/.test(hours)) return 'the hours field is not valid';
  if ((dom === '?') === (dow === '?')) return 'exactly one of day-of-month and day-of-week must be ?';
  return null;
}

/** Returns an error message, or null for a valid retention value. */
export function validateRetentionDays(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(n)) return 'data retention must be a whole number of days';
  if (n === 0) return null;
  if (n < MIN_RETENTION_DAYS || n > MAX_RETENTION_DAYS) {
    return `data retention must be 0 (never archive) or ${MIN_RETENTION_DAYS} to ${MAX_RETENTION_DAYS} days`;
  }
  return null;
}

/** The schedule and retention a stack should carry for this config (invalid values fall back to the defaults). */
export function stackSettingsFor(config) {
  const scheduleCron = validateSchedule(config?.scheduleCron) === null ? config.scheduleCron.trim() : DEFAULT_SCHEDULE;
  const retention = config?.dataRetentionDays;
  const dataRetentionDays = retention !== undefined && retention !== null && validateRetentionDays(retention) === null
    ? Number(retention) : DEFAULT_RETENTION_DAYS;
  return { scheduleCron, dataRetentionDays };
}

/** CloudFormation parameters for client-stack.yaml. */
export function stackSettingsParameters(config) {
  const { scheduleCron, dataRetentionDays } = stackSettingsFor(config);
  return [
    { ParameterKey: 'ScheduleExpression', ParameterValue: scheduleCron },
    { ParameterKey: 'DataRetentionDays', ParameterValue: String(dataRetentionDays) },
  ];
}

/**
 * For code pushes: the saved values when a config row exists, else keep
 * whatever the stack already has (a stack created outside the portal, for
 * example by deploy-client.sh, may carry its own schedule).
 */
export function stackSettingsParametersOrPrevious(config) {
  if (!config) {
    return [
      { ParameterKey: 'ScheduleExpression', UsePreviousValue: true },
      { ParameterKey: 'DataRetentionDays', UsePreviousValue: true },
    ];
  }
  return stackSettingsParameters(config);
}

/** Human description for the data reference and Settings. */
export function describeSchedule(expr) {
  const preset = SCHEDULE_PRESETS.find((p) => p.cron === expr);
  if (preset) return { label: preset.label.replace(' (recommended)', ''), time: preset.time, cadence: preset.cadence, custom: false };
  return { label: `Custom schedule ${expr}`, time: expr, cadence: 'on a custom schedule', custom: true };
}
